import React from 'react';
import InfoTooltip from '../../../components/InfoTooltip';

interface OrgDetailSectionProps {
    title: string;
    subtitle?: string;
    /** Short explanation rendered as an (i) tooltip next to the section title,
     *  for sections whose concept isn't self-explanatory (e.g. units). */
    help?: string;
    children: React.ReactNode;
    defaultCollapsed?: boolean;
    className?: string;
}

/**
 * Collapsible section card for the organization create/detail forms. Mirrors
 * the asset pages' `AssetDetailSection` visual pattern (card + chevron) but
 * lives in the organization namespace so the two surfaces stay decoupled.
 */
const OrgDetailSection: React.FC<OrgDetailSectionProps> = ({
    title,
    subtitle,
    help,
    children,
    defaultCollapsed = false,
    className = '',
}) => {
    const [collapsed, setCollapsed] = React.useState(defaultCollapsed);
    const toggle = () => setCollapsed(!collapsed);

    return (
        <section className={`bg-text-contrast rounded-[5px] shadow-sm px-5 py-4 ${className}`}>
            {/* Title and chevron are separate toggles so the optional help
                tooltip (itself a button) can sit next to the title without
                nesting buttons. */}
            <div className={'w-full flex items-center justify-between gap-2'}>
                <div className={'min-w-0'}>
                    <div className={'flex items-center gap-1.5'}>
                        <button
                            type={'button'}
                            onClick={toggle}
                            // flex-1 keeps the toggle spanning the row (minus the
                            // help icon), so splitting the header for the tooltip
                            // doesn't shrink the click target to the text itself.
                            className={'flex-1 min-w-0 text-left'}
                        >
                            <h2 className={'text-lg font-semibold text-foreground'}>{title}</h2>
                        </button>
                        {help && <InfoTooltip text={help} />}
                    </div>
                    {subtitle && (
                        <p className={'text-sm text-foreground/60 mt-0.5'}>{subtitle}</p>
                    )}
                </div>
                <button
                    type={'button'}
                    onClick={toggle}
                    aria-label={title}
                    className={'shrink-0'}
                >
                    <svg
                        className={`w-5 h-5 text-foreground/40 transition-transform ${collapsed ? '' : 'rotate-180'}`}
                        fill={'none'}
                        viewBox={'0 0 24 24'}
                        stroke={'currentColor'}
                        strokeWidth={2}
                    >
                        <path strokeLinecap={'round'} strokeLinejoin={'round'} d={'M19 9l-7 7-7-7'} />
                    </svg>
                </button>
            </div>
            {!collapsed && (
                <div className={'mt-4'}>
                    {children}
                </div>
            )}
        </section>
    );
};

export default OrgDetailSection;
